import React, { useState } from "react";
import { createPortal } from "react-dom";
import { saveAs } from "file-saver";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Printer,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import { Button } from "components/ui/Button";

import styles from "./DocumentPreviewModal.module.css";
import { DocumentViewer } from "./DocumentViewer";

// A document's own name, escaped: it reaches the print header, the tab and the filename offered on
// "Save as PDF", and it comes from a user-supplied upload.
const printTitleOf = (doc) =>
  String(doc?.fileName || doc?.name || "Document").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
  );

export const DocumentPreviewModal = ({
  isOpen,
  onClose,
  imageData,
  document: doc,
  onSendEmail,
  onReview,
}) => {
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 1;
  const isReviewed = doc?.status?.toUpperCase() === "REVIEWED";

  const handlePrint = () => {
    if (!imageData) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>${printTitleOf(doc)}</title>
          <style>
            body {
              margin: 0;
              padding: 0;
            }
            img {
              width: 100%;
              height: auto;
            }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <img src="${imageData}" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownload = async () => {
    if (!imageData) return;

    try {
      const fileName = doc?.fileName || doc?.name || "document";

      if (imageData.startsWith("data:")) {
        const [header, base64Data] = imageData.split(",");
        const mimeMatch = header.match(/data:([^;]+)/);
        const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });

        saveAs(blob, fileName);
      } else {
        const response = await fetch(imageData);
        const blob = await response.blob();
        saveAs(blob, fileName);
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleReview = () => {
    if (!isReviewed && onReview) {
      onReview(doc);
    }
  };

  const getFileName = () => {
    if (doc?.fileName) return doc.fileName;
    if (doc?.name) return doc.name;
    if (doc?.documentUrl) {
      const urlParts = doc.documentUrl.split("/");
      return urlParts[urlParts.length - 1].split("?")[0] || "document";
    }
    return "document";
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Document preview</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.topToolbar}>
            <p className={styles.fileName}>{getFileName()}</p>
            <div className={styles.toolbarActions}>
              <Button variant="text" className={styles.actionButton} onClick={handlePrint}>
                <span>Print</span>
                <Printer size={18} />
              </Button>
              <Button variant="text" className={styles.actionButton} onClick={handleDownload}>
                <span>Download</span>
                <Download size={18} />
              </Button>
            </div>
          </div>

          <div className={styles.viewerWrapper}>
            <div
              className={styles.viewerContent}
              style={{ transform: `scale(${zoom})` }}
            >
              <DocumentViewer
                imageData={imageData}
                isImage={doc?.imageType ?? true}
                fileName={doc?.fileName || doc?.name}
                fileType={doc?.fileType}
              />
            </div>
          </div>

          <div className={styles.bottomToolbar}>
            <div className={styles.zoomControls}>
              <button className={styles.zoomButton} onClick={handleZoomIn}>
                <ZoomIn size={24} />
              </button>
              <button className={styles.zoomButton} onClick={handleZoomOut}>
                <ZoomOut size={24} />
              </button>
            </div>

            <div className={styles.pageControls}>
              <button
                className={styles.pageButton}
                onClick={handlePrevPage}
                disabled={currentPage <= 1}
              >
                <ChevronLeft size={24} />
              </button>
              <span className={styles.pageInfo}>
                {currentPage}/{totalPages}
              </span>
              <button
                className={styles.pageButton}
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight size={24} />
              </button>
            </div>

            <div className={styles.zoomControls} style={{ opacity: 0 }}>
              <button className={styles.zoomButton}>
                <ZoomIn size={24} />
              </button>
              <button className={styles.zoomButton}>
                <ZoomOut size={24} />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <Button variant="outline" onClick={onSendEmail}>
              Send Email
            </Button>
          </div>
          <Button
            onClick={handleReview}
            disabled={isReviewed}
          >
            <span>{isReviewed ? "Reviewed" : "Review"}</span>
            {isReviewed && <Check size={18} />}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, window.document.body);
};
